import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Carousel } from "react-bootstrap";
import { propertiesService } from "../services/firebaseServices";
import HospitableWidget from "../components/HospitableWidget";
import SEO from "../components/SEO";

const PropertyDetail = () => {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const galleryRef = useRef(null);

  const seoMap = {
    "luxury-ski-in-ski-out-chalet-mountain-village-telluride": {
      title: "Luxury Ski-In / Ski-Out Chalet in Mountain Village, Telluride",
      description:
        "Luxury 5BR Telluride chalet on the Sundance trail. Features a private sauna, steam showers, and a 1,500 sq ft deck. Book direct for the best Mountain Village rates.",
    },
    "mountain-village-luxury-cabin": {
      title: "Luxury Telluride Cabin | Mountain Village, Telluride",
      description:
        "Modern 3BR Telluride log cabin in the heart of Mountain Village. Enjoy a 2-minute walk to the Peaks Resort ski valet and 4 minutes to the Gondola. Book direct for best rates.",
    },
    "luxury-resort-access-vacation-home-near-beaver-creek-avon": {
      title: "Beaver Creek Luxury Home at The Westin Resort | Avon, CO",
      description:
        "Luxury 4BR + Office home at The Westin Riverfront in Avon. Direct gondola access to Beaver Creek, saltwater pools, and ski valet.",
    },
    "luxury-coastal-vacation-home-del-mar-san-diego": {
      title: "Luxury Coastal Vacation Home in Del Mar | Near San Diego",
      description:
        "Del Mar luxury retreat featuring a private infinity pool and swim-up bar. This 4BR coastal home offers indoor-outdoor living near pristine beaches.",
    },
  };

  const richTextStyles = `
    .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
      font-family: 'Cinzel', serif !important;
      color: #002366;
      margin-top: 2rem;
      margin-bottom: 1rem;
      line-height: 1.3;
    }
    .prose h2 { font-size: 1.5rem; }
    .prose h3 { font-size: 1.25rem; }
    .prose, .prose p, .prose li, .prose div, .prose span, .prose ul, .prose ol {
      font-family: 'Montserrat', sans-serif !important;
      line-height: 1.75;
      color: #374151;
    }
    .prose p { margin-bottom: 1rem; }
    .prose ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1.5rem; }
    .prose ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1.5rem; }
    .prose strong { color: #002366; font-weight: 700; }
    @media (max-width: 768px) { .prose h2 { font-size: 1.25rem; } }

    .gallery-thumbs::-webkit-scrollbar { height: 4px; }
    .gallery-thumbs::-webkit-scrollbar-track { background: #f1f1f1; }
    .gallery-thumbs::-webkit-scrollbar-thumb { background: #002366; border-radius: 2px; }
  `;

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const all = await propertiesService.getAll();
        const found = all.find((p) => p.slug === slug || p.id === slug);
        setProperty(found);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [slug]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = lightboxOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [lightboxOpen]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  if (loading)
    return (
      <div className="pt-40 text-center font-secondary">
        Loading luxury home details...
      </div>
    );
  if (!property)
    return (
      <div className="pt-40 text-center font-secondary">
        Property not found.
      </div>
    );

  const galleryPhotos = property.photos || [];

  const prevImage = () =>
    setGalleryIndex((i) => (i - 1 + galleryPhotos.length) % galleryPhotos.length);
  const nextImage = () =>
    setGalleryIndex((i) => (i + 1) % galleryPhotos.length);

  const onTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 50) dx < 0 ? nextImage() : prevImage();
    setTouchStartX(null);
  };

  const openLightbox = (idx) => {
    setGalleryIndex(idx);
    setLightboxOpen(true);
  };

  const scrollToGallery = () => {
    galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pageSEO = seoMap[slug] || {
    title: property.title,
    description: property.headline,
  };

  return (
    <div className="pt-24">
      <SEO
        title={pageSEO.title}
        description={pageSEO.description}
        image={property.image}
        url={`/homes/${slug}`}
      />

      <style>{richTextStyles}</style>

      {/* Hero Carousel */}
      <div className="h-[70vh] w-full relative bg-gray-900 group">
        {galleryPhotos.length > 0 ? (
          <Carousel
            fade
            interval={3500}
            controls={true}
            indicators={false}
            className="h-full"
          >
            {galleryPhotos.slice(0, 5).map((photo, idx) => (
              <Carousel.Item key={idx} className="h-[70vh]">
                <img
                  src={photo.url}
                  alt={`Slide ${idx}`}
                  className="d-block w-100 h-100 object-cover opacity-90"
                />
              </Carousel.Item>
            ))}
          </Carousel>
        ) : property.image ? (
          <img
            src={property.image}
            alt={property.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            No Images Available
          </div>
        )}

        <div className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none z-10">
          <div className="text-center px-4">
            <h1 className="text-3xl md:text-5xl font-primary mb-4 shadow-sm tracking-wide text-white drop-shadow-md leading-tight">
              {property.title}
            </h1>
            <p className="font-secondary uppercase tracking-widest text-sm md:text-base text-gray-200 drop-shadow-sm">
              {property.location}
            </p>
          </div>
        </div>

        {/* See All Photos */}
        {galleryPhotos.length > 0 && (
          <button
            onClick={scrollToGallery}
            className="absolute bottom-5 left-5 md:bottom-8 md:left-8 z-20 flex items-center gap-2 bg-white/90 hover:bg-white text-acf-blue font-secondary text-xs md:text-sm uppercase tracking-widest px-4 py-2.5 md:px-5 md:py-3 rounded-sm shadow-lg transition-all duration-200 hover:shadow-xl"
            aria-label="See all photos"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 md:w-5 md:h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
              />
            </svg>
            See All Photos
          </button>
        )}
      </div>

      {/* Main Grid Layout */}
      <div className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 min-w-0">
          <div className="border-b border-gray-200 pb-8 mb-8">
            <h2 className="text-2xl font-primary text-acf-blue mb-4 leading-tight">
              {property.headline}
            </h2>

            <div className="flex flex-wrap gap-6 md:gap-12 text-sm md:text-base font-secondary text-gray-700 bg-acf-slate p-6 rounded-sm">
              <div className="flex flex-col">
                <span className="font-bold text-acf-blue uppercase text-xs tracking-wider mb-1">Guests</span>
                <span>{property.guests || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-acf-blue uppercase text-xs tracking-wider mb-1">Bedrooms</span>
                <span>{property.bedrooms || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-acf-blue uppercase text-xs tracking-wider mb-1">Beds</span>
                <span>{property.beds || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-acf-blue uppercase text-xs tracking-wider mb-1">Baths</span>
                <span>{property.baths || 0}</span>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="font-primary text-acf-blue text-2xl mb-6">
              About this Home
            </h2>
            <div
              className="prose font-secondary text-gray-600 text-lg break-words max-w-full overflow-hidden"
              dangerouslySetInnerHTML={{ __html: property.details }}
            />
          </div>

          {/* Gallery Carousel */}
          {galleryPhotos.length > 0 && (
            <div ref={galleryRef} id="gallery" className="mb-12 scroll-mt-28">
              <h2 className="font-primary text-acf-blue text-2xl mb-6">Gallery</h2>

              {/* Main Image */}
              <div
                className="relative w-full aspect-[4/3] md:aspect-[16/9] bg-gray-900 rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => openLightbox(galleryIndex)}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
              >
                <img
                  src={galleryPhotos[galleryIndex].url}
                  alt={`Gallery ${galleryIndex + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />

                {/* Fullscreen hint */}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="bg-black/60 rounded-full p-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </div>
                </div>

                {/* Prev arrow */}
                {galleryPhotos.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200 text-xl font-light z-10"
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                )}

                {/* Next arrow */}
                {galleryPhotos.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200 text-xl font-light z-10"
                    aria-label="Next image"
                  >
                    ›
                  </button>
                )}

                {/* Counter */}
                <div className="absolute bottom-3 right-4 bg-black/60 text-white text-xs font-secondary px-2 py-1 rounded-full">
                  {galleryIndex + 1} / {galleryPhotos.length}
                </div>
              </div>

              {/* Thumbnail Strip */}
              {galleryPhotos.length > 1 && (
                <div className="gallery-thumbs flex gap-2 overflow-x-auto py-3 mt-1">
                  {galleryPhotos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => setGalleryIndex(idx)}
                      className={`flex-shrink-0 w-20 h-14 md:w-24 md:h-16 rounded-md overflow-hidden border-2 transition-all duration-200 ${
                        galleryIndex === idx
                          ? "border-acf-gold opacity-100 scale-105"
                          : "border-transparent opacity-60 hover:opacity-90"
                      }`}
                      aria-label={`View image ${idx + 1}`}
                    >
                      <img
                        src={photo.url}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Property Tour Video */}
          {property.videoFile && (property.videoFile.url || property.videoFile.embedUrl) && (
            <div className="mb-12">
              <h2 className="font-primary text-acf-blue text-2xl mb-6">Property Tour</h2>
              <div className="w-full rounded-xl overflow-hidden bg-gray-900 aspect-[16/9]">
                {property.videoFile.type === "embed" || property.videoFile.embedUrl ? (
                  <iframe
                    src={property.videoFile.embedUrl || property.videoFile.url}
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="Property Tour"
                  />
                ) : (
                  <video
                    src={property.videoFile.url}
                    controls
                    className="w-full h-full object-contain"
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-28">
            <div className="bg-white shadow-xl border border-gray-100 p-0 rounded-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100 text-center">
                <h3 className="font-primary text-acf-blue mb-2 text-xl">
                  Reserve This Home
                </h3>
                <p className="font-secondary text-gray-500 text-sm">
                  Book direct for the best available rate.
                </p>
              </div>

              <HospitableWidget compact={true} propertyId={property.hospitableId} />

              {property.bookingUrl && (
                <div className="p-4 text-center bg-gray-50 border-t border-gray-100">
                  <span className="text-xs font-secondary text-gray-400 uppercase tracking-widest block mb-2">
                    Or view on
                  </span>
                  <a
                    href={property.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-acf-blue border-b border-acf-blue hover:text-acf-gold hover:border-acf-gold transition font-primary text-sm"
                  >
                    Direct Listing Page &rarr;
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Close */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-acf-gold transition-colors text-4xl leading-none z-10 w-10 h-10 flex items-center justify-center"
            aria-label="Close fullscreen"
          >
            ×
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/70 font-secondary text-sm z-10">
            {galleryIndex + 1} / {galleryPhotos.length}
          </div>

          {/* Prev */}
          {galleryPhotos.length > 1 && (
            <button
              onClick={prevImage}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 text-white hover:text-acf-gold transition-colors text-5xl md:text-6xl font-light z-10 w-12 h-12 flex items-center justify-center"
              aria-label="Previous image"
            >
              ‹
            </button>
          )}

          {/* Image */}
          <img
            src={galleryPhotos[galleryIndex].url}
            alt={`Fullscreen ${galleryIndex + 1}`}
            className="max-h-[90vh] max-w-[85vw] md:max-w-[88vw] object-contain select-none"
            draggable={false}
          />

          {/* Next */}
          {galleryPhotos.length > 1 && (
            <button
              onClick={nextImage}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 text-white hover:text-acf-gold transition-colors text-5xl md:text-6xl font-light z-10 w-12 h-12 flex items-center justify-center"
              aria-label="Next image"
            >
              ›
            </button>
          )}

          {/* Thumbnail strip in lightbox */}
          {galleryPhotos.length > 1 && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center">
              <div className="flex gap-1.5 overflow-x-auto px-4 max-w-[90vw]">
                {galleryPhotos.map((photo, idx) => (
                  <button
                    key={idx}
                    onClick={() => setGalleryIndex(idx)}
                    className={`flex-shrink-0 w-12 h-8 md:w-16 md:h-11 rounded overflow-hidden border-2 transition-all duration-200 ${
                      galleryIndex === idx
                        ? "border-acf-gold opacity-100"
                        : "border-transparent opacity-40 hover:opacity-70"
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  >
                    <img
                      src={photo.url}
                      alt={`Thumb ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
